/**
 * Walmart.com Detail Page
 * Analytics for Walmart Marketplace sales
 */

import React, { useState, useEffect } from 'react'
import { DollarSign, ShoppingCart, TrendingUp, Package, Target, MapPin, BarChart3, ChevronDown, ChevronUp, RefreshCw, Clock, Crown, Users } from 'lucide-react'
import { Link } from 'wouter'
import { useDtcKpis, formatDtcCurrency, formatDtcNumber, formatDtcPercent } from '../../lib/dtc-data-service'
import { CalendarRange, getCalendarDays } from '../../components/DateRangeSelector'
import DtcHeader from '../../components/DtcHeader'
import HeroKpiCard from '../../components/HeroKpiCard'
import DtcAIInsightsPanel from '../../components/DtcAIInsightsPanel'
import { DtcShopifySalesChart } from '../../components/DtcShopifySalesChart'

export default function WalmartDetail() {
  // Walmart platform ID (from our sync)
  const WALMART_PLATFORM_ID = '86a40402-dc88-419d-aab0-d3ba407234a9'
  
  // Date range state
  const [calendarRange, setCalendarRange] = useState<CalendarRange>('last7')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [activeCustomDates, setActiveCustomDates] = useState<{start: string, end: string} | null>(null)
  
  // Collapsible sections
  const [showRevenueTrend, setShowRevenueTrend] = useState(true)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  
  // Sync state
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null)
  
  // Fetch last sync time on mount
  useEffect(() => {
    fetch(`/api/dtc/sync-status?platform_id=${WALMART_PLATFORM_ID}`)
      .then(r => r.json())
      .then(data => {
        if (data.last_sync_at) {
          setLastSyncTime(data.last_sync_at)
        }
      })
      .catch(err => console.error('Failed to fetch sync status:', err))
  }, [])
  
  // 🔧 FIX: Calculate explicit start_date/end_date to match Overview Dashboard
  // This ensures consistent date ranges across all DTC pages (single source of truth)
  let apiOptions: any = { view_type: 'calendar' }
  let days = 30; // Default to 30 days
  
  if (calendarRange === 'custom' && activeCustomDates) {
    apiOptions.start_date = activeCustomDates.start
    apiOptions.end_date = activeCustomDates.end
    // Calculate days for custom range
    const start = new Date(activeCustomDates.start);
    const end = new Date(activeCustomDates.end);
    days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  } else if (calendarRange !== 'custom') {
    // Calculate explicit dates to match Overview Dashboard logic
    const end = new Date(); // Today
    days = getCalendarDays(calendarRange);
    const start = new Date(end);
    start.setDate(start.getDate() - days + 1); // +1 to make it inclusive (e.g., 30 days = today - 29)
    
    apiOptions.start_date = start.toISOString().split('T')[0];
    apiOptions.end_date = end.toISOString().split('T')[0];
    
    console.log('[Walmart Detail] Calculated date range:', {
      calendarRange,
      days,
      start_date: apiOptions.start_date,
      end_date: apiOptions.end_date
    })
  }
  
  // Fetch Walmart-specific KPIs
  const { data: kpiData, isLoading: kpisLoading, refetch } = useDtcKpis(
    WALMART_PLATFORM_ID,
    undefined, // No single date parameter
    apiOptions
  )
  
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setActiveCustomDates({ start: customStartDate, end: customEndDate })
    }
  }
  
  // Handle manual sync
  const handleSyncNow = async () => {
    setIsSyncing(true)
    setSyncMessage(null)
    
    try {
      const response = await fetch('/api/dtc/walmart-sync-simple', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platform_id: WALMART_PLATFORM_ID,
          start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 30 days
        }),
      })
      
      const data = await response.json()
      
      if (data.success) {
        setSyncMessage(`✅ Synced ${data.orders_fetched} orders ($${data.total_revenue.toFixed(2)})`)
        setLastSyncTime(new Date().toISOString())
        // Refetch KPIs to show updated data
        setTimeout(() => {
          refetch()
          setSyncMessage(null)
        }, 3000)
      } else {
        setSyncMessage('❌ Sync failed: ' + (data.error || 'Unknown error'))
      }
    } catch (error) {
      setSyncMessage('❌ Sync failed: ' + (error instanceof Error ? error.message : 'Network error'))
    } finally {
      setIsSyncing(false)
    }
  }

  const summary = kpiData?.summary

  // Walmart brand colors
  const walmartColor = { 
    primary: '#0071CE', 
    light: '#E3F2FD', 
    medium: '#90CAF9',
    dark: '#004F9A'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Walmart Color Accent Bar */}
      <div 
        className="h-1 w-full"
        style={{ backgroundColor: walmartColor.primary }}
      />
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <div className="max-w-[1400px] mx-auto">
          <nav className="flex items-center text-sm text-gray-600">
            <Link href="/dtc/overview">
              <a className="hover:text-purple-600 transition-colors">DTC Summary</a>
            </Link>
            <span className="mx-2">›</span>
            <span className="font-semibold" style={{ color: walmartColor.primary }}>
              Walmart.com
            </span>
          </nav>
        </div>
      </div>
      
      {/* Header */}
      <DtcHeader
        title="Walmart.com Detail"
        subtitle="Walmart Marketplace Analytics"
        onRefresh={refetch}
        isLoading={kpisLoading}
      />

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6 space-y-6">
        {/* Date Range Selector */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              📅 Date Range
            </span>
            {calendarRange !== 'custom' && apiOptions.start_date && apiOptions.end_date && (
              <span className="text-xs text-gray-600">
                {apiOptions.start_date} to {apiOptions.end_date}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button 
              onClick={() => { setCalendarRange('last7'); setActiveCustomDates(null); }} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last7' 
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              Last 7 Days
            </button>
            <button 
              onClick={() => { setCalendarRange('last30'); setActiveCustomDates(null); }} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last30' 
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              Last 30 Days
            </button>
            <button 
              onClick={() => { setCalendarRange('last90'); setActiveCustomDates(null); }} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last90' 
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              Last 90 Days
            </button>
            <button 
              onClick={() => setCalendarRange('custom')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'custom' 
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              📆 Custom Range
            </button>
          </div>

          {/* Custom Date Range Picker */}
          {calendarRange === 'custom' && (
            <div className="mt-4 p-4 bg-white rounded-lg border-2 border-blue-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || new Date().toISOString().split('T')[0]}
                    min="2025-08-01"
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate || '2025-08-01'}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
              <button
                onClick={handleApplyCustomRange}
                disabled={!customStartDate || !customEndDate}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-blue-700 hover:to-cyan-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${kpisLoading ? 'animate-spin' : ''}`} />
                {kpisLoading ? 'Loading...' : 'View Report'}
              </button>
              {customStartDate && customEndDate && (
                <p className="text-xs text-gray-600 mt-3 text-center">
                  Will show: <strong>{customStartDate}</strong> to <strong>{customEndDate}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Walmart Data Sync Section - Matches Shopify Layout */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-gray-400" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Walmart Data Sync</h3>
                {lastSyncTime && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Last synced: {new Date(lastSyncTime).toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}></div>
                <span className="text-sm font-medium text-gray-700">
                  {isSyncing ? 'Syncing' : 'Ready'}
                </span>
              </div>
              
              {/* Sync Now Button */}
              <button
                onClick={handleSyncNow}
                disabled={isSyncing}
                className="px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: walmartColor.primary }}
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                Sync Now
              </button>
            </div>
          </div>
          
          {/* Sync Message */}
          {syncMessage && (
            <div className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium ${
              syncMessage.startsWith('✅') 
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {syncMessage}
            </div>
          )}
        </div>

        {/* Hero KPI Cards - Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Total Revenue"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.total_revenue || 0)}
            icon={DollarSign}
            wowDeltaText={summary?.revenue_growth_prev !== null && summary?.revenue_growth_prev !== undefined 
              ? `${summary.revenue_growth_prev >= 0 ? '+' : ''}${(summary.revenue_growth_prev * 100).toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.revenue_growth_prev !== undefined && summary?.revenue_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.revenue_growth_yoy !== null && summary?.revenue_growth_yoy !== undefined 
              ? `${summary.revenue_growth_yoy >= 0 ? '+' : ''}${(summary.revenue_growth_yoy * 100).toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.revenue_growth_yoy !== undefined && summary?.revenue_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Total Orders"
            value={kpisLoading ? '...' : formatDtcNumber(summary?.total_orders || 0)}
            icon={ShoppingCart}
            wowDeltaText={summary?.orders_growth_prev !== null && summary?.orders_growth_prev !== undefined 
              ? `${summary.orders_growth_prev >= 0 ? '+' : ''}${(summary.orders_growth_prev * 100).toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.orders_growth_prev !== undefined && summary?.orders_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.orders_growth_yoy !== null && summary?.orders_growth_yoy !== undefined 
              ? `${summary.orders_growth_yoy >= 0 ? '+' : ''}${(summary.orders_growth_yoy * 100).toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.orders_growth_yoy !== undefined && summary?.orders_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Avg Order Value"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.avg_order_value || 0)}
            icon={Target}
            wowDeltaText={summary?.aov_growth_prev !== null && summary?.aov_growth_prev !== undefined 
              ? `${summary.aov_growth_prev >= 0 ? '+' : ''}${(summary.aov_growth_prev * 100).toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.aov_growth_prev !== undefined && summary?.aov_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.aov_growth_yoy !== null && summary?.aov_growth_yoy !== undefined 
              ? `${summary.aov_growth_yoy >= 0 ? '+' : ''}${(summary.aov_growth_yoy * 100).toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.aov_growth_yoy !== undefined && summary?.aov_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Total Units Sold"
            value={kpisLoading ? '...' : formatDtcNumber(summary?.total_units || 0)}
            icon={Package}
            wowDeltaText={summary?.units_growth_prev !== null && summary?.units_growth_prev !== undefined 
              ? `${summary.units_growth_prev >= 0 ? '+' : ''}${(summary.units_growth_prev * 100).toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.units_growth_prev !== undefined && summary?.units_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.units_growth_yoy !== null && summary?.units_growth_yoy !== undefined 
              ? `${summary.units_growth_yoy >= 0 ? '+' : ''}${(summary.units_growth_yoy * 100).toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.units_growth_yoy !== undefined && summary?.units_growth_yoy >= 0 ? 'success' : 'danger'}
          />
        </div>

        {/* Hero KPI Cards - Row 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Net Revenue"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.net_revenue || 0)}
            icon={DollarSign}
            yoyDeltaText="After discounts"
            yoyDeltaTone="success"
          />

          <HeroKpiCard
            label="Avg Daily Revenue"
            value={kpisLoading ? '...' : formatDtcCurrency((summary?.total_revenue || 0) / Math.max(days || 90, 1))}
            icon={TrendingUp}
            yoyDeltaText="Per day average"
            yoyDeltaTone="neutral"
          />

          <HeroKpiCard
            label="Avg Units per Order"
            value={kpisLoading ? '...' : ((summary?.total_units || 0) / Math.max(summary?.total_orders || 1, 1)).toFixed(1)}
            icon={Package}
            yoyDeltaText="Units/order"
            yoyDeltaTone="neutral"
          />

          <HeroKpiCard
            label="Active Days"
            value={kpiData?.trend?.length.toString() || '0'}
            icon={BarChart3}
            yoyDeltaText="Days with sales"
            yoyDeltaTone="neutral"
          />
        </div>

        {/* Walmart+ vs Regular Orders Section */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Walmart+ Member Orders
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              New Dec 2025
            </span>
          </h3>
          
          {/* Check if we have Walmart+ data */}
          {(summary?.walmart_plus_orders || 0) > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Walmart+ Orders */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border-2 border-yellow-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-yellow-400 rounded-lg">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Walmart+ Orders</p>
                    <p className="text-xs text-yellow-600">Premium member purchases</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-3xl font-bold text-yellow-900">
                      {formatDtcNumber(summary?.walmart_plus_orders || 0)}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">Orders</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-yellow-900">
                      {formatDtcCurrency(summary?.walmart_plus_revenue || 0)}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">Revenue</p>
                  </div>
                </div>
                {(summary?.total_orders || 0) > 0 && (
                  <div className="mt-4 pt-3 border-t border-yellow-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-yellow-700">Share of orders</span>
                      <span className="font-semibold text-yellow-900">
                        {((summary?.walmart_plus_orders || 0) / (summary?.total_orders || 1) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 bg-yellow-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full"
                        style={{ width: `${Math.min(100, (summary?.walmart_plus_orders || 0) / (summary?.total_orders || 1) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Regular Orders */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-gray-400 rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Regular Orders</p>
                    <p className="text-xs text-gray-600">Standard customer purchases</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatDtcNumber((summary?.total_orders || 0) - (summary?.walmart_plus_orders || 0))}
                    </p>
                    <p className="text-xs text-gray-700 mt-1">Orders</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatDtcCurrency((summary?.total_revenue || 0) - (summary?.walmart_plus_revenue || 0))}
                    </p>
                    <p className="text-xs text-gray-700 mt-1">Revenue</p>
                  </div>
                </div>
                {(summary?.total_orders || 0) > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Share of orders</span>
                      <span className="font-semibold text-gray-900">
                        {(((summary?.total_orders || 0) - (summary?.walmart_plus_orders || 0)) / (summary?.total_orders || 1) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gray-500 rounded-full"
                        style={{ width: `${Math.min(100, ((summary?.total_orders || 0) - (summary?.walmart_plus_orders || 0)) / (summary?.total_orders || 1) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Walmart+ Tracking Coming Soon</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Starting December 25, 2025, Walmart's API will identify Walmart+ member orders. 
                    Once data is available, you'll see a breakdown of Walmart+ vs regular customer orders here.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    💡 Walmart+ members typically have higher order values and loyalty
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* AI Insights Panel for Walmart */}
        {apiOptions.start_date && apiOptions.end_date && (
          <DtcAIInsightsPanel
            platformId={WALMART_PLATFORM_ID}
            startDate={apiOptions.start_date}
            endDate={apiOptions.end_date}
            maxInsights={8}
          />
        )}

        {/* Sales Chart - Walmart Blue */}
        {kpiData?.trend && kpiData.trend.length > 0 && (
          <DtcShopifySalesChart
            data={kpiData.trend.map(d => ({
              ...d,
              avg_order_value: d.avg_order_value || (d.total_orders > 0 ? d.total_revenue / d.total_orders : 0)
            }))}
            platformColor={walmartColor.primary}
            platformName="Walmart"
            showLyComparison={true}
            showLwComparison={true}
          />
        )}

        {/* Revenue Trend - Collapsible */}
        {kpiData?.trend && kpiData.trend.length > 0 && (
          <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setShowRevenueTrend(!showRevenueTrend)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" style={{ color: walmartColor.primary }} />
                Daily Sales Trend ({kpiData.trend.length} days)
              </h3>
              {showRevenueTrend ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {showRevenueTrend && (
              <div className="px-6 pb-6 border-t border-gray-200">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Date</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Units</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">AOV</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {kpiData.trend.slice().reverse().map((point, idx) => {
                        const dateStr = point.date.includes('T') ? point.date : point.date + 'T00:00:00Z'
                        return (
                        <tr key={idx} className="hover:bg-blue-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                            {new Date(dateStr).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric',
                              timeZone: 'UTC'
                            })}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">
                            {formatDtcCurrency(point.total_revenue)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-700">
                            {formatDtcNumber(point.total_orders)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-700">
                            {formatDtcNumber(point.total_units)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium" style={{ color: walmartColor.primary }}>
                            {formatDtcCurrency(point.avg_order_value)}
                            </td>
                          </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 text-xs text-gray-500 text-center">
                  Showing {kpiData.trend.length} days • Click to collapse
                </div>
              </div>
            )}
          </div>
        )}

        {/* Order Insights */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" style={{ color: walmartColor.primary }} />
            Order Insights
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: walmartColor.light }}>
              <p className="text-2xl font-bold text-gray-900">
                {formatDtcNumber(summary?.total_orders || 0)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Total Orders</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-900">
                {formatDtcCurrency(summary?.avg_order_value || 0)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Avg Order Value</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-900">
                {formatDtcNumber(summary?.total_units || 0)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Units Sold</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-900">
                {((summary?.total_units || 0) / Math.max(summary?.total_orders || 1, 1)).toFixed(1)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Units per Order</p>
            </div>
          </div>
        </div>

        {/* Walmart Marketplace Info */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-800 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Walmart Marketplace</h2>
              <p className="text-blue-100 text-sm mt-1">Your products on Walmart.com</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sales Performance */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp className="w-6 h-6 text-cyan-300" />
                <h3 className="font-semibold text-lg">Sales Performance</h3>
              </div>
              <p className="text-sm text-blue-100 mb-3">
                Tracking {summary?.total_orders || 0} orders with {formatDtcCurrency(summary?.total_revenue || 0)} revenue
              </p>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-medium rounded-full border border-green-400/30">
                  ✓ Synced
                </span>
              </div>
            </div>

            {/* Product Reach */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <Package className="w-6 h-6 text-pink-300" />
                <h3 className="font-semibold text-lg">Product Reach</h3>
              </div>
              <p className="text-sm text-blue-100 mb-3">
                Your Buttah products available to millions of Walmart.com shoppers
              </p>
            </div>

            {/* Data Source */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-6 h-6 text-emerald-300" />
                <h3 className="font-semibold text-lg">Marketplace API</h3>
              </div>
              <p className="text-sm text-blue-100 mb-3">
                Real-time order data from Walmart Seller Central API
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <p className="text-sm text-blue-100">
              💡 <strong>Auto-synced from Walmart API</strong> • Order data updates automatically • Track performance in real-time
            </p>
          </div>
        </div>

        {/* Note about available data */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Data Source:</strong> Walmart Marketplace API provides order-level data including revenue, units, products, and shipping details. Customer lifetime value and email/SMS data are not available via Walmart's API.
          </p>
        </div>
      </div>
    </div>
  )
}

